import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Calendar, MapPin, Mail } from "lucide-react";
import PageMeta from "@/components/PageMeta";

const KIND_LABEL = { event: "Event", promotion: "Promotion", editorial: "Editorial" };

export function Campaigns() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/campaigns").then(r => setItems(r.data)); }, []);
  return (
    <div className="section-dark min-h-screen pt-32 pb-16">
      <PageMeta title="Campaigns · Marketing & Events" description="Promotional campaigns, private events and member benefits from the LOOXS house — Chapter Openings 2026, the Wedding Season Atelier and the Members' First Look." />
      <div className="max-w-[1400px] mx-auto px-8 md:px-16">
        <div className="text-center mb-24">
          <p className="text-[10px] tracking-luxury uppercase text-gold mb-6">Marketing & Events</p>
          <h1 className="font-editorial text-5xl md:text-8xl leading-[0.95] tracking-tight">Campaigns</h1>
          <div className="rule-gold w-32 mx-auto my-10" />
          <p className="max-w-2xl mx-auto text-ivory/70 leading-relaxed">
            Promotional campaigns, private events and member benefits from the LOOXS house. All by invitation or appointment.
          </p>
        </div>

        <div className="space-y-24">
          {items.map((c, i) => (
            <article
              key={c.slug}
              data-testid={`campaign-${c.slug}`}
              className="block animate-fade-up"
              style={{ animationDelay: `${i*80}ms` }}
            >
              <div className={`grid md:grid-cols-12 gap-8 items-center ${i%2===0 ? "" : "md:[direction:rtl]"}`}>
                <Link to={`/campaigns/${c.slug}`} className="group md:col-span-7 [direction:ltr] relative aspect-[16/10] overflow-hidden hover-shift">
                  <img src={c.hero_image} alt={c.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-1000" />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(10,10,10,0.45), transparent 55%)" }} />
                </Link>
                <div className="md:col-span-5 [direction:ltr]">
                  <p className="text-[10px] tracking-widest-plus uppercase text-gold mb-4">{KIND_LABEL[c.kind] || "Campaign"}</p>
                  <h2 className="font-editorial text-4xl md:text-6xl leading-[1] tracking-tight">{c.title}</h2>
                  {c.subtitle && <p className="mt-4 font-editorial-italic text-xl md:text-2xl text-ivory/70">{c.subtitle}</p>}

                  <div className="mt-8 space-y-2 text-sm text-ivory/70">
                    {c.event_date && (
                      <p data-testid={`campaign-${c.slug}-date`} className="flex items-center gap-3">
                        <Calendar className="w-4 h-4 text-gold" strokeWidth={1.25} /> {c.event_date}
                      </p>
                    )}
                    {c.event_location && (
                      <p data-testid={`campaign-${c.slug}-location`} className="flex items-center gap-3">
                        <MapPin className="w-4 h-4 text-gold" strokeWidth={1.25} /> {c.event_location}
                      </p>
                    )}
                  </div>

                  <div className="mt-8 flex flex-wrap items-center gap-6">
                    <Link to={`/campaigns/${c.slug}`} className="tracking-widest-plus text-[11px] uppercase text-gold link-draw">Read more →</Link>
                    {c.cta_email && (
                      <a
                        data-testid={`campaign-${c.slug}-rsvp`}
                        href={`mailto:${c.cta_email}?subject=${encodeURIComponent(c.title + " — Enquiry")}`}
                        className="inline-flex items-center gap-2 bg-gold text-ink px-6 py-3 text-[11px] tracking-widest-plus uppercase hover:bg-goldDark hover:text-ivory transition-colors duration-500"
                      >
                        <Mail className="w-4 h-4" strokeWidth={1.5} />
                        {c.cta_label || "Enquire"}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}

export function CampaignDetail() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  useEffect(() => { api.get(`/campaigns/${slug}`).then(r => setC(r.data)).catch(() => setC({ error: true })); }, [slug]);
  if (!c) return <div className="section-dark min-h-screen flex items-center justify-center text-ivory/50">Loading…</div>;
  if (c.error) return <div className="section-dark min-h-screen flex items-center justify-center">Campaign not found</div>;

  return (
    <div className="section-dark">
      <PageMeta title={c.title} description={(c.subtitle || c.body || "").slice(0, 155)} />
      <section className="relative h-screen overflow-hidden">
        <img src={c.hero_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70 animate-slow-zoom" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-ink/30 to-ink" />
        <div className="grain" />
        <div className="relative h-full flex flex-col justify-end px-8 md:px-16 pb-16 max-w-[1400px] mx-auto">
          <p className="text-[10px] tracking-luxury uppercase text-gold mb-4">{KIND_LABEL[c.kind] || "Campaign"}</p>
          <h1 className="font-editorial text-6xl md:text-9xl leading-[0.9] tracking-tight">{c.title}</h1>
          {c.subtitle && <p className="mt-4 font-editorial-italic text-2xl md:text-4xl text-ivory/80">{c.subtitle}</p>}
        </div>
      </section>

      <section className="bg-onyx py-24 md:py-32">
        <div className="max-w-3xl mx-auto text-center px-8">
          <p className="font-editorial text-2xl md:text-3xl leading-relaxed text-ivory/85">{c.body}</p>

          {(c.event_date || c.event_location) && (
            <div className="mt-14 pt-10 border-t border-ivory/15 grid md:grid-cols-2 gap-8 text-left max-w-2xl mx-auto">
              {c.event_date && (
                <div>
                  <p className="text-[10px] tracking-widest-plus uppercase text-gold mb-2">When</p>
                  <p className="font-editorial text-xl text-ivory">{c.event_date}</p>
                </div>
              )}
              {c.event_location && (
                <div>
                  <p className="text-[10px] tracking-widest-plus uppercase text-gold mb-2">Where</p>
                  <p className="font-editorial text-xl text-ivory">{c.event_location}</p>
                </div>
              )}
            </div>
          )}

          {c.cta_email && (
            <a
              data-testid="campaign-detail-rsvp"
              href={`mailto:${c.cta_email}?subject=${encodeURIComponent(c.title + " — Enquiry")}`}
              className="mt-14 inline-flex items-center gap-3 bg-gold text-ink px-10 py-4 text-[11px] tracking-widest-plus uppercase hover:bg-goldDark hover:text-ivory transition-colors duration-500"
            >
              <Mail className="w-4 h-4" strokeWidth={1.5} />
              {c.cta_label || "Enquire with concierge"}
            </a>
          )}
        </div>
      </section>

      {c.gallery?.length > 0 && (
        <section className="bg-ink py-16">
          <div className="max-w-[1400px] mx-auto px-8 md:px-16 grid md:grid-cols-2 gap-4">
            {c.gallery.map((g, i) => (
              <div key={`${c.slug}-${g}-${i}`} className="aspect-[4/5] overflow-hidden hover-shift">
                <img src={g} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
